import { useEffect, useMemo, useState } from "react";
import { Coins, Download, Filter, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type CreditAuditRow, type Identity, type Resp, type Summary,
  PAGE_SIZE, fmtNum,
} from "./creditAudit/types";
import { CreditAuditFilters } from "./creditAudit/CreditAuditFilters";
import { CreditAuditTable } from "./creditAudit/CreditAuditTable";

export function CreditAuditTab() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<CreditAuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [identities, setIdentities] = useState<Record<string, Identity>>({});
  const [summary, setSummary] = useState<Summary | null>(null);
  const [offset, setOffset] = useState(0);
  const [exporting, setExporting] = useState(false);

  const [category, setCategory] = useState<string>("all");
  const [action, setAction] = useState<string>("all");
  const [adminUserId, setAdminUserId] = useState("");
  const [targetUserId, setTargetUserId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const buildBody = (overrides?: { offset?: number; format?: "csv" }) => {
    const body: Record<string, unknown> = {};
    if (category !== "all") body.category = category;
    if (action !== "all") body.action = action;
    if (adminUserId.trim()) body.admin_user_id = adminUserId.trim();
    if (targetUserId.trim()) body.target_user_id = targetUserId.trim();
    if (batchId.trim()) body.batch_id = batchId.trim();
    if (fromDate) body.from = new Date(fromDate).toISOString();
    if (toDate) body.to = new Date(`${toDate}T23:59:59`).toISOString();
    if (overrides?.format) {
      body.format = overrides.format;
    } else {
      body.limit = PAGE_SIZE;
      body.offset = overrides?.offset ?? offset;
    }
    return body;
  };

  const load = async (nextOffset = 0) => {
    setLoading(true);
    setOffset(nextOffset);
    try {
      const { data, error } = await supabase.functions.invoke(
        "admin-credit-audit",
        { body: buildBody({ offset: nextOffset }) },
      );
      if (error) throw new Error(error.message);
      const payload = data as Resp;
      if (payload?.error) throw new Error(payload.error);
      setRows(payload.rows ?? []);
      setTotal(payload.total ?? 0);
      setIdentities(payload.identities ?? {});
      setSummary(payload.summary ?? null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load Audit Log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "admin-credit-audit",
        { body: buildBody({ format: "csv" }) },
      );
      if (error) throw new Error(error.message);
      const blob =
        data instanceof Blob
          ? data
          : new Blob([typeof data === "string" ? data : JSON.stringify(data)], {
              type: "text/csv;charset=utf-8",
            });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `credit-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "CSV export failed");
    } finally {
      setExporting(false);
    }
  };

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + rows.length, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  const summaryNote = useMemo(() => {
    if (!summary) return null;
    const { granted, reversed, net, grant_count, reversal_count } = summary;
    return `Page totals: ${fmtNum(granted)} granted (${grant_count}) · ${fmtNum(reversed)} reversed (${reversal_count}) · net ${fmtNum(net)}`;
  }, [summary]);

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Coins className="h-5 w-5" /> Credit Audit Log
        </CardTitle>
        <CardDescription>
          All credit grants, reversals, and bulk-grant summaries from{" "}
          <code className="font-mono text-xs">admin_actions</code>. Filter by
          category, actor, target, batch, or date range, and export to CSV (up
          to 5,000 rows per export).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <CreditAuditFilters
          category={category} setCategory={setCategory}
          action={action} setAction={setAction}
          batchId={batchId} setBatchId={setBatchId}
          adminUserId={adminUserId} setAdminUserId={setAdminUserId}
          targetUserId={targetUserId} setTargetUserId={setTargetUserId}
          fromDate={fromDate} setFromDate={setFromDate}
          toDate={toDate} setToDate={setToDate}
        />

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => void load(0)} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Filter className="h-4 w-4" />}
            Apply filters
          </Button>
          <Button variant="outline" onClick={() => void load(offset)} disabled={loading}>
            <RefreshCcw className="h-4 w-4" /> Refresh
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={exporting}>
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </Button>
          <span className="ml-auto text-xs text-muted-foreground">{summaryNote}</span>
        </div>

        {loading && <Skeleton className="h-48 w-full" />}

        {!loading && rows.length === 0 && (
          <p className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
            No credit actions match these filters.
          </p>
        )}

        {!loading && rows.length > 0 && (
          <CreditAuditTable rows={rows} identities={identities} />
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {total === 0 ? "0 rows" : `Showing ${pageStart}–${pageEnd} of ${fmtNum(total)}`}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm" variant="outline" disabled={!canPrev || loading}
              onClick={() => void load(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              size="sm" variant="outline" disabled={!canNext || loading}
              onClick={() => void load(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
